import { Injectable } from '@angular/core';

import { AuthService } from './auth.service';

/** Chave no `sessionStorage`: sobrevive a sair da tela e a recarregar a aba, não a fechar a aba. */
export const UNCERTAIN_TRANSFERS_KEY = 'SMSFUNNEL::UNCERTAIN_TRANSFERS';

/**
 * Validade de um pedido incerto guardado: 24 h. No backend a `idempotency_key` de transferência
 * não expira (`wallet_transfers` e `unit_transfer_keys` não são podados), então o teto aqui é do
 * aviso, não da chave: depois de um dia o admin já conferiu o extrato, e repetir é pedido novo.
 */
export const UNCERTAIN_TRANSFER_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Pedido de transferência que ficou sem resposta conclusiva (status 0, 5xx): pode ter sido
 * registrado. Vale até um desfecho definitivo — editar o formulário ou sair da tela não o apaga.
 */
export interface UncertainTransfer {
  /** `produto|origem|destino|quantidade` (ver `AddCreditsComponent.transferFingerprint`). */
  fingerprint: string;
  /**
   * A chave com que ele foi: repetir o mesmo pedido vai com ela. `null` se o backend a recusou
   * (`idempotency_key_reused`) — aí repetir passa pela confirmação.
   */
  idempotencyKey: string | null;
  /** O que foi pedido, para o aviso: "R$ 10,00" ou "1000 créditos de SMS". */
  label: string;
  destinationLabel: string;
}

/** Como fica no `sessionStorage`: com o dono (quem estava logado e quem era simulado) e quando. */
interface StoredUncertainTransfer extends UncertainTransfer {
  owner: string;
  saved_at: number;
}

/**
 * Transferências com desfecho incerto, FORA da tela (review da task L, 07/10).
 *
 * Enquanto viviam no componente, sair da tela e voltar apagava o pedido incerto e a chave dele:
 * repetir o MESMO pedido saía com `idempotency_key` nova, e o backend podia transferir duas
 * vezes. Aqui o pedido fica por fingerprint até um desfecho definitivo, e a tela reaproveita a
 * chave e mostra o aviso ao voltar para o mesmo par.
 *
 * Cada pedido tem dono e validade (rodada 5): só vale para quem o fez — o admin logado e o
 * cliente que ele simulava, se simulava — e por `UNCERTAIN_TRANSFER_TTL_MS`. O de outro dono é
 * ignorado (e preservado, para quando ele voltar na mesma aba); o vencido é apagado.
 */
@Injectable({ providedIn: 'root' })
export class UncertainTransferStore {

  constructor(private authService: AuthService) {}

  /**
   * `owner` é o dono capturado no ENVIO (`currentOwner()`): a resposta pode chegar depois de o admin
   * começar a simular outro cliente, e aí o dono "de agora" seria o errado (rodada 6). Sem ele, o
   * dono atual — que é o certo para ler o que a tela mostra.
   */
  find(fingerprint: string | null, owner = this.currentOwner()): UncertainTransfer | null {
    return fingerprint ? this.ownedBy(owner).find(entry => entry.fingerprint === fingerprint) || null : null;
  }

  /** O pedido incerto mais recente de um par (`produto|origem|destino`), de qualquer quantidade. */
  findForPair(pair: string | null): UncertainTransfer | null {
    if (!pair) {
      return null;
    }

    const matches = this.ownedBy(this.currentOwner()).filter(entry => entry.fingerprint.startsWith(`${pair}|`));
    return matches.length ? matches[matches.length - 1] : null;
  }

  save(entry: UncertainTransfer, owner = this.currentOwner()): void {
    const { fingerprint, idempotencyKey, label, destinationLabel } = entry;
    this.write([
      ...this.readFresh().filter(other => !(other.owner === owner && other.fingerprint === fingerprint)),
      { fingerprint, idempotencyKey, label, destinationLabel, owner, saved_at: Date.now() },
    ]);
  }

  remove(fingerprint: string, owner = this.currentOwner()): void {
    this.write(this.readFresh().filter(entry => !(entry.owner === owner && entry.fingerprint === fingerprint)));
  }

  /** `logado|simulado`: o admin e, se ele simula alguém, o cliente simulado. */
  currentOwner(): string {
    return `${this.authService.getUser(true)?.id ?? ''}|${this.authService.getUser()?.id ?? ''}`;
  }

  private ownedBy(owner: string): StoredUncertainTransfer[] {
    return this.readFresh().filter(entry => entry.owner === owner);
  }

  /** Grava a lista inteira (já sem os vencidos, que `readFresh` descarta). */
  private write(entries: StoredUncertainTransfer[]): void {
    if (entries.length) {
      sessionStorage.setItem(UNCERTAIN_TRANSFERS_KEY, JSON.stringify(entries));
    } else {
      sessionStorage.removeItem(UNCERTAIN_TRANSFERS_KEY);
    }
  }

  private readFresh(): StoredUncertainTransfer[] {
    const now = Date.now();

    try {
      const entries = JSON.parse(sessionStorage.getItem(UNCERTAIN_TRANSFERS_KEY) || '[]');
      return Array.isArray(entries)
        ? entries.filter(entry => typeof entry?.saved_at === 'number' && now - entry.saved_at < UNCERTAIN_TRANSFER_TTL_MS)
        : [];
    } catch {
      return [];
    }
  }
}
